import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  VideoLibrary as VideoLibraryIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PhotoCamera as PhotoCameraIcon,
  Bookmark as BookmarkIcon,
  ScreenShare as ScreenShareIcon,
} from '@mui/icons-material';

// Sample video data
interface VideoRecord {
  id: string;
  vehicleId: string;
  vehicleName: string;
  dateRecorded: string;
  duration: string;
  size: string;
  status: 'available' | 'processing' | 'failed';
  thumbnailUrl: string;
  tags: string[];
}

// Sample camera data
interface Camera {
  id: string;
  name: string;
  vehicleId: string;
  vehicleName: string;
  status: 'online' | 'offline' | 'recording';
  lastActive: string;
  location?: string;
  type: 'front' | 'rear' | 'interior' | 'side';
  streamUrl: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`video-tabpanel-${index}`}
      aria-labelledby={`video-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const VideoRecording: React.FC = () => {
  // State for tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Videos state
  const [videos, setVideos] = useState<VideoRecord[]>([
    {
      id: 'v1',
      vehicleId: 'vehicle1',
      vehicleName: 'Truck 1',
      dateRecorded: '2023-06-15T14:30:00Z',
      duration: '10:23',
      size: '45.8 MB',
      status: 'available',
      thumbnailUrl: 'https://via.placeholder.com/120x80',
      tags: ['incident', 'traffic']
    },
    {
      id: 'v2',
      vehicleId: 'vehicle2',
      vehicleName: 'Van 3',
      dateRecorded: '2023-06-14T09:45:00Z',
      duration: '5:17',
      size: '22.3 MB',
      status: 'available',
      thumbnailUrl: 'https://via.placeholder.com/120x80',
      tags: ['parking']
    },
    {
      id: 'v3',
      vehicleId: 'vehicle3',
      vehicleName: 'Truck 5',
      dateRecorded: '2023-06-13T16:20:00Z',
      duration: '8:42',
      size: '38.5 MB',
      status: 'processing',
      thumbnailUrl: 'https://via.placeholder.com/120x80',
      tags: []
    },
    {
      id: 'v4',
      vehicleId: 'vehicle1',
      vehicleName: 'Truck 1',
      dateRecorded: '2023-06-12T11:05:00Z',
      duration: '3:51',
      size: '18.2 MB',
      status: 'available',
      thumbnailUrl: 'https://via.placeholder.com/120x80',
      tags: ['maintenance']
    },
    {
      id: 'v5',
      vehicleId: 'vehicle4',
      vehicleName: 'Van 7',
      dateRecorded: '2023-06-10T13:15:00Z',
      duration: '12:09',
      size: '53.7 MB',
      status: 'failed',
      thumbnailUrl: 'https://via.placeholder.com/120x80',
      tags: []
    }
  ]);
  
  // Cameras state
  const [cameras, setCameras] = useState<Camera[]>([
    {
      id: 'c1',
      name: 'Front Camera',
      vehicleId: 'vehicle1',
      vehicleName: 'Truck 1',
      status: 'online',
      lastActive: '2023-06-15T15:30:00Z',
      location: 'Front Bumper',
      type: 'front',
      streamUrl: 'https://via.placeholder.com/320x240'
    },
    {
      id: 'c2',
      name: 'Rear Camera',
      vehicleId: 'vehicle1',
      vehicleName: 'Truck 1',
      status: 'online',
      lastActive: '2023-06-15T15:30:00Z',
      location: 'Rear Bumper',
      type: 'rear',
      streamUrl: 'https://via.placeholder.com/320x240'
    },
    {
      id: 'c3',
      name: 'Interior Camera',
      vehicleId: 'vehicle1',
      vehicleName: 'Truck 1',
      status: 'offline',
      lastActive: '2023-06-14T18:45:00Z',
      location: 'Dashboard',
      type: 'interior',
      streamUrl: 'https://via.placeholder.com/320x240'
    },
    {
      id: 'c4',
      name: 'Front Camera',
      vehicleId: 'vehicle2',
      vehicleName: 'Van 3',
      status: 'recording',
      lastActive: '2023-06-15T15:28:00Z',
      location: 'Front Windshield',
      type: 'front',
      streamUrl: 'https://via.placeholder.com/320x240'
    },
    {
      id: 'c5',
      name: 'Side Camera',
      vehicleId: 'vehicle3',
      vehicleName: 'Truck 5',
      status: 'online',
      lastActive: '2023-06-15T15:25:00Z',
      location: 'Right Side',
      type: 'side',
      streamUrl: 'https://via.placeholder.com/320x240'
    }
  ]);
  
  // Pagination state for videos
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  
  // Dialog states
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoRecord | null>(null);
  // const [isRecording, setIsRecording] = useState(false);
  // const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  
  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Filter videos based on search and filters
  const filteredVideos = videos.filter(video => {
    const matchesSearch = 
      video.vehicleName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      video.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
    const matchesStatus = 
      statusFilter === 'all' || 
      video.status === statusFilter;
      
    const matchesVehicle = 
      vehicleFilter === 'all' || 
      video.vehicleId === vehicleFilter;
      
    return matchesSearch && matchesStatus && matchesVehicle;
  });
  
  // Open video dialog
  const handleOpenVideoDialog = (video: VideoRecord) => {
    setSelectedVideo(video);
    setVideoDialogOpen(true);
  };
  
  // Close video dialog
  const handleCloseVideoDialog = () => {
    setVideoDialogOpen(false);
    setSelectedVideo(null);
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };
  
  // Handle vehicle filter change
  const handleVehicleFilterChange = (event: SelectChangeEvent) => {
    setVehicleFilter(event.target.value);
  };
  
  // Start recording
  const handleStartRecording = (camera: Camera) => {
    // setSelectedCamera(camera);
    // setIsRecording(true);
    
    // Update camera status to recording
    const updatedCameras = cameras.map(c => 
      c.id === camera.id ? { ...c, status: 'recording' as const } : c
    );
    setCameras(updatedCameras);
  };
  
  // Stop recording
  const handleStopRecording = (camera: Camera) => {
    // setIsRecording(false);
    
    // Update camera status to online
    const updatedCameras = cameras.map(c => 
      c.id === camera.id ? { ...c, status: 'online' as const } : c
    );
    setCameras(updatedCameras);
    
    // Add new video record (mock)
    const newVideo: VideoRecord = {
      id: `v${videos.length + 1}`,
      vehicleId: camera.vehicleId,
      vehicleName: camera.vehicleName,
      dateRecorded: new Date().toISOString(),
      duration: '0:30', // Mock duration
      size: '15.2 MB', // Mock size
      status: 'processing',
      thumbnailUrl: 'https://via.placeholder.com/120x80',
      tags: []
    };
    
    setVideos([newVideo, ...videos]);
  };
  
  // Delete video
  const handleDeleteVideo = (id: string) => {
    setVideos(videos.filter(video => video.id !== id));
  };
  
  // Status chip color
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'available':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'recording':
        return 'warning';
      default:
        return 'default';
    }
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Video Recording
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="video recording tabs"
          >
            <Tab 
              icon={<VideoLibraryIcon />} 
              iconPosition="start" 
              label="Recorded Videos" 
              id="video-tab-0" 
              aria-controls="video-tabpanel-0" 
            />
            <Tab 
              icon={<VideocamIcon />} 
              iconPosition="start" 
              label="Live Cameras" 
              id="video-tab-1" 
              aria-controls="video-tabpanel-1" 
            />
          </Tabs>
        </Box>
        
        {/* Recorded Videos Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search videos"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: '200px' }}
            />
            
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel>Vehicle</InputLabel>
              <Select
                value={vehicleFilter}
                label="Vehicle"
                onChange={handleVehicleFilterChange}
              >
                <MenuItem value="all">All Vehicles</MenuItem>
                <MenuItem value="vehicle1">Truck 1</MenuItem>
                <MenuItem value="vehicle2">Van 3</MenuItem>
                <MenuItem value="vehicle3">Truck 5</MenuItem>
                <MenuItem value="vehicle4">Van 7</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="videos table">
              <TableHead>
                <TableRow>
                  <TableCell>Thumbnail</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Date Recorded</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVideos
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((video) => (
                    <TableRow key={video.id} hover>
                      <TableCell>
                        <img 
                          src={video.thumbnailUrl} 
                          alt={`${video.vehicleName} video thumbnail`} 
                          style={{ width: '120px', height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => handleOpenVideoDialog(video)}
                        />
                      </TableCell>
                      <TableCell>{video.vehicleName}</TableCell>
                      <TableCell>{new Date(video.dateRecorded).toLocaleString()}</TableCell>
                      <TableCell>{video.duration}</TableCell>
                      <TableCell>{video.size}</TableCell>
                      <TableCell>
                        <Chip 
                          label={video.status} 
                          size="small" 
                          color={getStatusColor(video.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {video.tags.map(tag => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                          {video.tags.length === 0 && <Typography variant="body2">-</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenVideoDialog(video)}
                          disabled={video.status !== 'available'}
                          title="View"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          disabled={video.status !== 'available'}
                          title="Download"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteVideo(video.id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredVideos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
        
        {/* Live Cameras Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Cameras
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View and control cameras installed in your fleet vehicles. Start recording or take snapshots as needed.
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 3 }}>
              {cameras.map((camera) => (
                <Card key={camera.id} variant="outlined">
                  <Box sx={{ position: 'relative' }}>
                    <img 
                      src={camera.streamUrl} 
                      alt={`${camera.name} - ${camera.vehicleName}`}
                      style={{ width: '100%', height: '240px', objectFit: 'cover' }}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 10, 
                        right: 10,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Box
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%',
                          bgcolor: 
                            camera.status === 'online' ? 'success.main' : 
                            camera.status === 'recording' ? 'warning.main' : 'error.main',
                          animation: camera.status === 'recording' ? 'pulse 1.5s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                            '100%': { opacity: 1 },
                          }
                        }}
                      />
                      <Typography variant="caption">
                        {camera.status === 'recording' ? 'RECORDING' : camera.status.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {camera.name} - {camera.vehicleName}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Type: {camera.type.charAt(0).toUpperCase() + camera.type.slice(1)}
                      </Typography>
                      {camera.location && (
                        <Typography variant="body2">
                          Location: {camera.location}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        Last active: {new Date(camera.lastActive).toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions>
                    {camera.status === 'recording' ? (
                      <Button 
                        size="small" 
                        startIcon={<PauseIcon />} 
                        color="warning"
                        onClick={() => handleStopRecording(camera)}
                        disabled={false}
                      >
                        Stop Recording
                      </Button>
                    ) : (
                      <Button 
                        size="small" 
                        startIcon={<VideocamIcon />} 
                        color="primary"
                        onClick={() => handleStartRecording(camera)}
                        disabled={camera.status !== 'online'}
                      >
                        Start Recording
                      </Button>
                    )}
                    
                    <Button 
                      size="small" 
                      startIcon={<PhotoCameraIcon />}
                      disabled={camera.status === 'offline'}
                    >
                      Snapshot
                    </Button>
                    
                    <Button 
                      size="small" 
                      startIcon={<ScreenShareIcon />}
                      disabled={camera.status === 'offline'}
                    >
                      Fullscreen
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          </Box>
        </TabPanel>
      </Box>
      
      {/* Video Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleCloseVideoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedVideo?.vehicleName} - {selectedVideo && new Date(selectedVideo.dateRecorded).toLocaleString()}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: '100%', position: 'relative' }}>
            {/* Placeholder for video player */}
            <Box 
              sx={{ 
                width: '100%', 
                height: '400px', 
                bgcolor: 'black', 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PlayIcon sx={{ fontSize: 64, color: 'white', opacity: 0.7 }} />
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<PlayIcon />}>Play</Button>
                <Button startIcon={<DownloadIcon />}>Download</Button>
                <Button startIcon={<BookmarkIcon />}>Add Tag</Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                Duration: {selectedVideo?.duration} | Size: {selectedVideo?.size}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVideoDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoRecording; 